import React from 'react';

import Page from 'sharedreact/components/Page';
import { Tab } from 'sharedreact/components/Sidebar';
import SidebarLayout from 'sharedreact/components/SidebarLayout';
import PageNotFound404ErrorContainer from 'sharedreact/containers/PageNotFound404ErrorContainer';
import { translate } from 'sharedreact/utils/LanguageSupport';

import AdminStatusbarContainer from 'workspaces/launcher/containers/admin/AdminStatusbarContainer';
import AgentsContainer from 'workspaces/launcher/containers/admin/AgentsContainer';
import AuditContainer from 'workspaces/launcher/containers/admin/AuditContainer';
import FontsContainer from 'workspaces/launcher/containers/admin/FontsContainer';
import IdentityProvidersTabContainer from 'workspaces/launcher/containers/admin/IdentityProvidersTabContainer';
import LoadHistoryContainer from 'workspaces/launcher/containers/admin/LoadHistoryContainer';
import TokenDetailsContainer from 'workspaces/launcher/containers/admin/TokenDetailsContainer';
import TokensContainer from 'workspaces/launcher/containers/admin/TokensContainer';
import UsageTrackingContainer from 'workspaces/launcher/containers/admin/UsageTrackingContainer';
import ViewSessionsContainer from 'workspaces/launcher/containers/admin/ViewSessionsContainer';

type Props = {
  language: string,
  activeTab: string,
  action: string,
  userId: string,
  canManageTokens: boolean,
  canManageAgents: boolean

  onSelectTab: (tab: string) => void
};

class AdminPage extends React.Component<Props> {
  constructor (props: Props) {
    super(props);

    this.handleSelectTab = this.handleSelectTab.bind(this);
  }

  handleSelectTab (tabIndex: number): void {
    const tabNames: string[] = this.getTabNames();
    this.props.onSelectTab(tabNames[tabIndex] || '');
  }

  getTabNames (): string[] {
    let tabNames = [
      'usage'
    ];

    if (this.props.canManageTokens) {
      tabNames.push('managetokens');
    }

    tabNames.push('managefonts', 'idp', 'loadhistory', 'viewsessions');

    if (this.props.canManageAgents) {
      tabNames.push('manageagents');
    }

    tabNames.push('audit');

    return tabNames;
  }

  override render (): React.ReactNode {
    const { language, activeTab, action, userId, canManageTokens, canManageAgents } = this.props;

    let tabs: Tab[] = [{id: 'ae_tab_admin_usage', name: translate('launcher.usage.title')}];

    if (canManageTokens) {
      tabs.push({id: 'ae_tab_admin_tokens', name: translate('launcher.admin.managetokens.title')});
    }

    tabs.push(
        {id: 'ae_tab_admin_fonts', name: translate('launcher.admin.managefonts.title')},
        {id: 'ae_tab_admin_idp', name: translate('launcher.admin.idp.title')},
        {id: 'ae_tab_admin_loadhistory', name: translate('launcher.admin.loadhistory.title')},
        {id: 'ae_tab_admin_viewsessions', name: translate('launcher.admin.viewsessions.title')}
    );

    if (canManageAgents) {
      tabs.push({id: 'ae_tab_admin_agents', name: translate('launcher.admin.manageagents.title')});
    }

    tabs.push({id: 'ae_tab_admin_audit', name: translate('launcher.admin.audit.title')});

    const tabNames: string[] = this.getTabNames();

    const activeIndex = tabNames.indexOf(activeTab || 'usage');

    const tokenDetails = ((activeIndex === 1) && (action === 'create' || action === 'edit'));

    return (
      <Page className='webappslauncher-pagecontainer'>
        <SidebarLayout tabs={tabs}
          listClassName='webappslauncher-page-sidebar-list'
          onSelectTab={this.handleSelectTab}
          activeTab={activeIndex}>
          <UsageTrackingContainer index={tabNames.indexOf('usage')} activeTab={activeIndex} />
          {
            (canManageTokens && (tabNames[activeIndex] === 'managetokens')) // only get tokens when token tab selected
            ? (
              tokenDetails
              ? <TokenDetailsContainer index={tabNames.indexOf('managetokens')} activeTab={activeIndex}
                action={action}
                userId={userId} />
              : <TokensContainer index={tabNames.indexOf('managetokens')} activeTab={activeIndex} />
            )
            : null
          }
          <FontsContainer index={tabNames.indexOf('managefonts')} activeTab={activeIndex} />
          <IdentityProvidersTabContainer index={tabNames.indexOf('idp')} activeTab={activeIndex} />
          <LoadHistoryContainer index={tabNames.indexOf('loadhistory')} activeTab={activeIndex} language={language} />
          <ViewSessionsContainer index={tabNames.indexOf('viewsessions')} activeTab={activeIndex} />
          {canManageAgents ? <AgentsContainer index={tabNames.indexOf('manageagents')} activeTab={activeIndex} /> : undefined}
          <AuditContainer index={tabNames.indexOf('audit')} activeTab={activeIndex} />
          {activeIndex < 0 || activeIndex >= tabs.length
            ? <PageNotFound404ErrorContainer />
            : null
          }
        </SidebarLayout>
        <AdminStatusbarContainer tab={activeTab} />
      </Page>
    );
  }
}

export default AdminPage;
